import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Chip, TextField } from '@mui/material';
import { format, parseISO } from 'date-fns';
import useService from '../../hooks/service.hook';
import EquipmentSharingService from '../../services/equipment-sharing.service';
import { useSnackbar } from 'notistack';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { UserAvatar } from '../user-avatar/user-avatar.component';

interface Manager {
  id: number;
  email: string | null;
  firstname: string | null;
  lastname: string | null;
  profile_image: string | null;
  created_at: string | null;
}

interface PendingInvitation {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  created_at: string;
  status: string;
  invitation_type?: 'owner_to_manager' | 'owner_transfer' | 'manager_to_athlete';
  athlete_user_id?: number | null;
}

export interface EquipmentManagersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful invite, revoke, or cancel (refresh parent lists). */
  onManagersChanged?: () => void;
}

const EquipmentManagersDialog: FC<EquipmentManagersDialogProps> = ({ isOpen, onClose, onManagersChanged }) => {
  const equipmentSharingService = useService(EquipmentSharingService);
  const { enqueueSnackbar } = useSnackbar();

  const [managers, setManagers] = useState<Manager[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmailError, setInviteEmailError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const managerInvitations = useMemo(
    () => pendingInvitations.filter((i) => i.invitation_type === 'owner_to_manager'),
    [pendingInvitations]
  );

  useEffect(() => {
    if (isOpen) {
      fetchManagers();
      fetchPendingInvitations();
    }
  }, [isOpen]);

  const fetchManagers = () => {
    setManagersLoading(true);
    equipmentSharingService
      .getAthleteManagers()
      .then((res) => {
        setManagers(res.data.data || []);
        setManagersLoading(false);
      })
      .catch((err) => {
        setManagersLoading(false);
        let errorMessage = 'Failed to fetch managers. Please try again later';
        if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to view equipment managers.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const fetchPendingInvitations = () => {
    setInvitationsLoading(true);
    equipmentSharingService
      .getSentEquipmentInvitations()
      .then((res) => {
        setPendingInvitations(res.data.data || []);
        setInvitationsLoading(false);
      })
      .catch((err) => {
        setInvitationsLoading(false);
        let errorMessage = 'Failed to fetch pending invitations. Please try again later';
        if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to view invitations.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const handleRevokeAccess = (managerId: number, managerName: string) => {
    if (window.confirm(`Are you sure you want to revoke access for ${managerName}?`)) {
      equipmentSharingService
        .revokeAthleteManager(managerId)
        .then(() => {
          enqueueSnackbar('Successfully revoked manager access', {
            autoHideDuration: 3000,
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
          fetchManagers();
          onManagersChanged?.();
        })
        .catch((err) => {
          let errorMessage = 'Failed to revoke manager access. Please try again later';
          if (err?.response?.status === 400) {
            errorMessage = err?.response?.data?.message || 'Cannot revoke access.';
          } else if (err?.response?.status === 403) {
            errorMessage = 'You do not have permission to revoke this manager.';
          } else if (err?.response?.status === 404) {
            errorMessage = 'Manager not found.';
          }
          enqueueSnackbar(errorMessage, {
            autoHideDuration: 3000,
            variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        });
    }
  };

  const handleCancelInvitation = (invitationId: number, email: string) => {
    if (window.confirm(`Are you sure you want to cancel the invitation for ${email}?`)) {
      equipmentSharingService
        .cancelEquipmentInvitation(invitationId)
        .then(() => {
          enqueueSnackbar('Successfully canceled invitation', {
            autoHideDuration: 3000,
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
          fetchPendingInvitations();
          onManagersChanged?.();
        })
        .catch((err) => {
          let errorMessage = 'Failed to cancel invitation. Please try again later';
          if (err?.response?.status === 400) {
            errorMessage = 'This invitation is no longer pending.';
          } else if (err?.response?.status === 403) {
            errorMessage = 'You do not have permission to cancel this invitation.';
          } else if (err?.response?.status === 404) {
            errorMessage = 'Invitation not found.';
          }
          enqueueSnackbar(errorMessage, {
            autoHideDuration: 3000,
            variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        });
    }
  };

  const handleInviteSubmit = () => {
    setInviteEmailError('');
    if (isEmailInvalid(inviteEmail)) {
      setInviteEmailError('This email is invalid. Please use a valid email');
      return;
    }
    setInviteLoading(true);
    equipmentSharingService
      .sendEquipmentInvitation('owner_to_manager', inviteEmail)
      .then(() => {
        enqueueSnackbar('Successfully invited equipment manager', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        setInviteEmail('');
        fetchPendingInvitations();
        onManagersChanged?.();
      })
      .catch((err) => {
        let errorMessage = 'Failed to invite equipment manager. Please try again later';
        if (err?.response?.status === 400) {
          errorMessage = err?.response?.data?.message || 'Invalid request. Please check the email address.';
        } else if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to invite managers.';
        } else if (err?.response?.status === 404) {
          errorMessage = 'That email is not registered on CoeLab yet.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      })
      .finally(() => {
        setInviteLoading(false);
      });
  };

  const renderManagers = () => {
    if (managersLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (managers.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 4,
          }}
        >
          <SupervisorAccountIcon sx={{ fontSize: 60, color: 'grey' }} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No managers
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {managers.map((manager) => {
          const fullName = `${manager.firstname ?? ''} ${manager.lastname ?? ''}`.trim() || manager.email || 'Manager';
          const secondaryParts = [
            manager.email,
            manager.created_at ? `Added: ${format(parseISO(manager.created_at), 'MMM do, Y')}` : null,
          ].filter(Boolean);
          return (
            <ListItem
              key={manager.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="revoke access"
                  onClick={() => handleRevokeAccess(manager.id, fullName)}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <UserAvatar src={manager.profile_image} name={fullName} userId={manager.id} />
              </ListItemAvatar>
              <ListItemText primary={fullName} secondary={secondaryParts.join(' • ')} />
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderPendingInvitations = () => {
    if (invitationsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (managerInvitations.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 4,
          }}
        >
          <MailOutlineIcon sx={{ fontSize: 60, color: 'grey' }} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No pending invitations
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {managerInvitations.map((invitation) => {
          const displayName =
            invitation.firstname && invitation.lastname
              ? `${invitation.firstname} ${invitation.lastname}`
              : invitation.email;
          return (
            <ListItem
              key={invitation.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="cancel invitation"
                  onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <UserAvatar name={displayName} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography>{displayName}</Typography>
                    <Chip
                      label="Manager Invitation"
                      color="default"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                secondary={`${invitation.email} • Sent: ${format(parseISO(invitation.created_at), 'MMM do, Y')}`}
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Equipment Managers</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <TextField
                label="Add Manager"
                value={inviteEmail}
                size="small"
                onChange={(e) => setInviteEmail(e.target.value)}
                error={!!inviteEmailError}
                helperText={inviteEmailError || 'Enter the email of the user to invite'}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleInviteSubmit}
                disabled={inviteLoading}
                sx={{ mt: 0.5 }}
              >
                Invite
              </Button>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Current Managers
            </Typography>
            {renderManagers()}
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pending Invitations
            </Typography>
            {renderPendingInvitations()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentManagersDialog;
